<template>
  <div>
    <h3 class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center ga-2">
      <v-icon
        size="18"
        color="primary"
      >
        mdi-account
      </v-icon>
      {{ $t('onboarding.wizard.data-entry.public-facing-ambassador') }}
    </h3>

    <OnboardingFormField
      class="mb-4"
      field-key="ambassadorSalutation"
      :label="$t('onboarding.wizard.data-entry.salutation')"
    >
      <v-select
        :model-value="formData.ambassadorSalutation"
        id="onboarding-field-ambassadorSalutation"
        data-ai-field="ambassadorSalutation"
        :class="fieldClass('ambassadorSalutation')"
        :items="salutationItems"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        clearable
        hide-details
        :placeholder="$t('onboarding.wizard.data-entry.salutation-placeholder')"
        @focus="onFieldFocus('ambassadorSalutation')"
        @update:model-value="updateField('ambassadorSalutation', $event ?? '')"
      />
    </OnboardingFormField>

    <v-row class="mb-4">
      <v-col
        cols="12"
        md="6"
      >
        <OnboardingFormField
          field-key="ambassadorFirstName"
          :label="$t('onboarding.wizard.data-entry.contact-first-name')"
        >
          <v-text-field
            :model-value="formData.ambassadorFirstName"
            id="onboarding-field-ambassadorFirstName"
            data-ai-field="ambassadorFirstName"
            :class="fieldClass('ambassadorFirstName')"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            :placeholder="$t('onboarding.wizard.data-entry.first-name-placeholder')"
            hide-details
            @focus="onFieldFocus('ambassadorFirstName')"
            @update:model-value="updateField('ambassadorFirstName', $event)"
          />
        </OnboardingFormField>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <OnboardingFormField
          field-key="ambassadorLastName"
          :label="$t('onboarding.wizard.data-entry.contact-last-name')"
        >
          <v-text-field
            :model-value="formData.ambassadorLastName"
            id="onboarding-field-ambassadorLastName"
            data-ai-field="ambassadorLastName"
            :class="fieldClass('ambassadorLastName')"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            :placeholder="$t('onboarding.wizard.data-entry.last-name-placeholder')"
            hide-details
            @focus="onFieldFocus('ambassadorLastName')"
            @update:model-value="updateField('ambassadorLastName', $event)"
          />
        </OnboardingFormField>
      </v-col>
    </v-row>

    <OnboardingFormField
      class="mb-4"
      field-key="phone"
      icon="mdi-phone"
      :label="$t('onboarding.wizard.data-entry.phone')"
    >
      <div
        data-ai-field="phone"
        :class="fieldClass('phone')"
        @focusin="onFieldFocus('phone')"
      >
        <PhoneField
          :model-value="formData.phone"
          id="onboarding-field-phone"
          :label="$t('onboarding.wizard.data-entry.phone')"
          update-on-input
          validate-phone
          :country-column-size="{ cols: 5, md: 4, xl: 4 }"
          :phone-column-size="{ cols: 7, md: 8, xl: 8 }"
          @update:model-value="updateField('phone', $event || '')"
        />
      </div>
    </OnboardingFormField>

    <OnboardingFormField
      class="mb-4"
      field-key="ambassadorMobile"
      icon="mdi-cellphone"
      :label="$t('onboarding.wizard.data-entry.mobile')"
    >
      <div
        data-ai-field="ambassadorMobile"
        :class="fieldClass('ambassadorMobile')"
        @focusin="onFieldFocus('ambassadorMobile')"
      >
        <PhoneField
          :model-value="formData.ambassadorMobile"
          id="onboarding-field-ambassadorMobile"
          :label="$t('onboarding.wizard.data-entry.mobile')"
          update-on-input
          validate-phone
          :country-column-size="{ cols: 5, md: 4, xl: 4 }"
          :phone-column-size="{ cols: 7, md: 8, xl: 8 }"
          @update:model-value="updateField('ambassadorMobile', $event || '')"
        />
      </div>
    </OnboardingFormField>

    <OnboardingFormField
      class="mb-4"
      field-key="ambassadorRole"
      :label="$t('onboarding.submit.fields.role')"
    >
      <v-text-field
        :model-value="formData.ambassadorRole"
        id="onboarding-field-ambassadorRole"
        data-ai-field="ambassadorRole"
        :class="fieldClass('ambassadorRole')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :placeholder="$t('onboarding.wizard.data-entry.role-placeholder')"
        hide-details
        @focus="onFieldFocus('ambassadorRole')"
        @update:model-value="updateField('ambassadorRole', $event)"
      />
    </OnboardingFormField>

    <OnboardingFormField
      class="mb-4"
      field-key="ambassadorEmail"
      :label="$t('onboarding.submit.fields.email')"
    >
      <v-text-field
        :model-value="formData.ambassadorEmail"
        id="onboarding-field-ambassadorEmail"
        data-ai-field="ambassadorEmail"
        :class="fieldClass('ambassadorEmail')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        type="email"
        autocomplete="email"
        :rules="[emailRule]"
        :placeholder="$t('onboarding.wizard.data-entry.email-placeholder')"
        hide-details="auto"
        @focus="onFieldFocus('ambassadorEmail')"
        @update:model-value="updateField('ambassadorEmail', $event)"
      />
    </OnboardingFormField>

    <OnboardingFormField
      v-if="isCommissionEditable"
      class="mb-6"
      field-key="commission"
      icon="mdi-percent"
      :label="$t('onboarding.wizard.data-entry.commission-rate')"
    >
      <v-text-field
        :model-value="formData.commission"
        id="onboarding-field-commission"
        data-ai-field="commission"
        :class="fieldClass('commission')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :placeholder="$t('onboarding.wizard.data-entry.commission-placeholder')"
        hide-details
        @focus="onFieldFocus('commission')"
        @update:model-value="updateField('commission', $event)"
      />
    </OnboardingFormField>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PhoneField from '@/components/PhoneField.vue'
import { email as emailRule } from '@/lib/validation'

import { useOnboardingDataEntryContext } from '../../composables/onboardingDataEntryContext'
import { useOnboardingFeatures } from '../../composables/useOnboardingFeatures'
import OnboardingFormField from './OnboardingFormField.vue'

const { formData, updateField, fieldClass, onFieldFocus } = useOnboardingDataEntryContext()
const { t } = useI18n()
const { isCommissionEditable } = useOnboardingFeatures()

const salutationItems = computed(() => [
  { title: t('onboarding.wizard.data-entry.salutation-mr'), value: 'Herr' },
  { title: t('onboarding.wizard.data-entry.salutation-ms'), value: 'Frau' },
])
</script>
